/**
 * DomicilioAddressStep.tsx
 *
 * Etapa exclusiva para agendas de atendimento a domicílio.
 * Coleta o endereço onde o atendimento será realizado.
 *
 * Comportamentos:
 *  - Checkbox "Preencher com endereço do paciente": busca o paciente pelo CPF
 *    e preenche os campos automaticamente a partir de patient.address[0].
 *  - Busca de CEP via ViaCEP ao sair do campo (onBlur).
 *  - Os dados são salvos no campo "domicilioAddress" do form e persistidos
 *    como extension valueAddress no Appointment criado.
 */

import { useState, useRef, useEffect } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useWatch } from "react-hook-form";
import { FormButtonGroup, FormTextInput } from "../../../../components/form";
import { GetAddressByCep } from "../../../../services/viaCep/GetCep";
import { GetPatient } from "../../../../services/fhir";
import { useFormatter } from "../../../../utils/useFormatter";
import { oids } from "../../../../configs/Settings";

// Campos obrigatórios validados antes de avançar para a confirmação
const REQUIRED_FIELDS = [
  "domicilioAddress.cep",
  "domicilioAddress.city",
  "domicilioAddress.state",
  "domicilioAddress.street",
] as const;

interface IDomicilioAddressStepProps {
  control: any;
  setValue: any;
  /** trigger do react-hook-form: dispara a validação dos campos antes de avançar */
  trigger: (fields?: any) => Promise<boolean>;
  onNext: () => void;
  onBack: () => void;
}

export function DomicilioAddressStep({
  control,
  setValue,
  trigger,
  onNext,
  onBack,
}: IDomicilioAddressStepProps) {
  const [loadingCep, setLoadingCep] = useState(false);
  const [usePatientAddress, setUsePatientAddress] = useState(false);
  // Endereço do paciente buscado na montagem — null = ainda não buscou, undefined = não tem endereço
  const [patientAddress, setPatientAddress] = useState<fhir5.Address | null | undefined>(null);
  const numberRef = useRef<HTMLInputElement>(null);

  // CPF do paciente vem do campo "cpf" do form pai
  const cpf = useWatch({ name: "cpf", control });

  // Ao montar, busca o paciente e verifica se possui endereço cadastrado.
  // O resultado controla a visibilidade do checkbox de preenchimento automático.
  useEffect(() => {
    const checkPatientAddress = async () => {
      const identifier = cpf?.replace(/\D/g, "");
      if (!identifier) {
        setPatientAddress(undefined);
        return;
      }
      try {
        const response = await GetPatient({ identifier });
        // address[0] pode existir ou não — undefined sinaliza ausência de endereço
        setPatientAddress(response?.data?.address?.[0] ?? undefined);
      } catch {
        setPatientAddress(undefined);
      }
    };

    checkPatientAddress();
  }, [cpf]);

  // Valida os campos obrigatórios antes de avançar
  const handleNext = async () => {
    const isValid = await trigger(REQUIRED_FIELDS);
    if (isValid) onNext();
  };

  // Preenche os campos de endereço com os dados vindos do ViaCEP
  const fillFromCep = async (rawCep: string) => {
    const cep = rawCep?.replace(/\D/g, "");
    if (cep?.length !== 8) return;

    setLoadingCep(true);
    try {
      const res = await GetAddressByCep(cep);
      if (res?.erro) return;

      setValue("domicilioAddress.city", res?.localidade || "");
      setValue("domicilioAddress.state", res?.uf || "");
      setValue("domicilioAddress.street", res?.logradouro || "");
      setValue("domicilioAddress.zone", res?.bairro || "");
      setValue("domicilioAddress.complement", res?.complemento || "");
      // Foca no campo número após preencher o endereço
      setTimeout(() => numberRef.current?.focus(), 100);
    } finally {
      setLoadingCep(false);
    }
  };

  // Preenche os campos com o endereço do paciente (já buscado no useEffect).
  // Ao desmarcar, limpa todos os campos de endereço.
  const handleUsePatientAddress = (checked: boolean) => {
    setUsePatientAddress(checked);

    if (!checked) {
      // Desmarcou: limpa todos os campos
      setValue("domicilioAddress.cep", "");
      setValue("domicilioAddress.city", "");
      setValue("domicilioAddress.state", "");
      setValue("domicilioAddress.street", "");
      setValue("domicilioAddress.number", "");
      setValue("domicilioAddress.complement", "");
      setValue("domicilioAddress.zone", "");
      return;
    }

    // Reutiliza o endereço já carregado na montagem — sem nova chamada à API
    if (!patientAddress) return;

    setValue("domicilioAddress.cep", patientAddress.postalCode || "");
    setValue("domicilioAddress.city", patientAddress.city || "");
    setValue("domicilioAddress.state", patientAddress.state || "");
    // Em FHIR, line[0] costuma ser logradouro, line[1] número, line[2] complemento
    setValue("domicilioAddress.street", patientAddress.line?.[0] || "");
    setValue("domicilioAddress.number", patientAddress.line?.[1] || "");
    setValue("domicilioAddress.complement", patientAddress.line?.[2] || "");
    setValue("domicilioAddress.zone", patientAddress.district || "");
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
        Informe o endereço onde o atendimento a domicílio será realizado.
      </Typography>

      {/* Checkbox para preencher com endereço do paciente.
          Exibido apenas quando o paciente possui endereço cadastrado no FHIR. */}
      {patientAddress && (
        <Box mb={2} display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                checked={usePatientAddress}
                onChange={(e) => handleUsePatientAddress(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                Preencher automaticamente com o endereço do paciente
              </Typography>
            }
          />
        </Box>
      )}

      <Grid container spacing={2}>
        {/* CEP */}
        <Grid item xs={12} sm={3}>
          <Controller
            name="domicilioAddress.cep"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label="CEP"
                variant="filled"
                fullWidth
                value={useFormatter.formatCEP(value)}
                onChange={(e) => onChange(useFormatter.formatCEP(e.target.value))}
                onBlur={() => fillFromCep(value)}
                error={!!error}
                sx={{ "& .MuiFilledInput-root": { backgroundColor: "neutral100.main" } }}
                InputProps={{
                  endAdornment: loadingCep ? (
                    <InputAdornment position="end">
                      <CircularProgress size={18} />
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}
          />
        </Grid>

        {/* Cidade */}
        <Grid item xs={12} sm={5}>
          <FormTextInput
            name="domicilioAddress.city"
            label="Cidade *"
            control={control}
            variant="filled"
            validate={(v: string) => !!v?.trim()}
          />
        </Grid>

        {/* Estado */}
        <Grid item xs={12} sm={4}>
          <FormTextInput
            name="domicilioAddress.state"
            label="Estado *"
            control={control}
            variant="filled"
            validate={(v: string) => !!v?.trim()}
          />
        </Grid>

        {/* Bairro */}
        <Grid item xs={12} sm={4}>
          <FormTextInput
            name="domicilioAddress.zone"
            label="Bairro"
            control={control}
            variant="filled"
          />
        </Grid>

        {/* Logradouro */}
        <Grid item xs={12} sm={8}>
          <FormTextInput
            name="domicilioAddress.street"
            label="Endereço *"
            control={control}
            variant="filled"
            validate={(v: string) => !!v?.trim()}
          />
        </Grid>

        {/* Número */}
        <Grid item xs={12} sm={4}>
          <FormTextInput
            inputRef={numberRef}
            name="domicilioAddress.number"
            label="Número"
            control={control}
            variant="filled"
          />
        </Grid>

        {/* Complemento */}
        <Grid item xs={12} sm={8}>
          <FormTextInput
            name="domicilioAddress.complement"
            label="Complemento"
            control={control}
            variant="filled"
          />
        </Grid>
      </Grid>

      <Box mt={3}>
        <FormButtonGroup
          goBackButtonText="Voltar"
          onGoBackButtonClick={onBack}
          nextButtonText="Próximo"
          onNextButtonClick={handleNext}
          loading={loadingCep}
        />
      </Box>
    </Box>
  );
}
